import api from '../../axios';
import { handleApiError } from '../../errorHandler';
import {
  IFuelsIndicatorsEthanol,
  ICreateFuelsIndicatorsEthanolRequest,
  IFilterOptions,
  IUpdateFuelsIndicatorsEthanolRequest,
} from './types';

const BASE_URL = '/fuels-indicators-ethanol';

/** Busca todos os indicadores de etanol */
export const getFuelsIndicatorsEthanol = async (): Promise<IFuelsIndicatorsEthanol[]> => {
  try {
    const response = await api.get(BASE_URL);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar indicadores de etanol:', error);
    throw handleApiError(error);
  }
};

/** Busca um indicador de etanol específico por ID */
export const getFuelsIndicatorsEthanolById = async (
  id: string
): Promise<IFuelsIndicatorsEthanol> => {
  try {
    const response = await api.get(`${BASE_URL}/${id}`);
    return response.data;
  } catch (error: any) {
    console.error(`Erro ao buscar indicador de etanol com ID ${id}:`, error);

    if (error.response?.status === 404) {
      throw new Error(`Indicador de etanol não encontrado`);
    }

    throw handleApiError(error);
  }
};

/** Cria um novo indicador de etanol */
export const createFuelsIndicatorsEthanol = async (
  data: ICreateFuelsIndicatorsEthanolRequest
): Promise<IFuelsIndicatorsEthanol> => {
  try {
    // Garantir que os números sejam do tipo number
    const formattedData = {
      ...data,
      plantCost: Number(data.plantCost) || 0,
      dailyVariation: Number(data.dailyVariation) || 0,
      dailyVariationValue: Number(data.dailyVariationValue) || 0,
      monthlyVariation: Number(data.monthlyVariation) || 0,
      monthlyVariationValue: Number(data.monthlyVariationValue) || 0,
    };

    const response = await api.post(BASE_URL, formattedData);
    // Código 201 indica que o recurso foi criado com sucesso
    return response.data;
  } catch (error: any) {
    console.error('Erro detalhado na criação do indicador:', {
      error: {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
      },
      response: error.response?.data,
      requestData: JSON.parse(error.config?.data || '{}'),
    });

    if (error.response?.status === 400) {
      throw new Error('Dados inválidos para criação do indicador de etanol');
    }

    if (error.response?.status === 500) {
      console.error('Dados enviados que causaram erro 500:', error.config?.data);
      throw new Error(
        'Erro interno do servidor ao criar indicador. Por favor, verifique os dados e tente novamente.'
      );
    }

    throw handleApiError(error);
  }
};

/** Atualiza um indicador de etanol existente */
export const updateFuelsIndicatorsEthanol = async (
  id: string,
  data: IUpdateFuelsIndicatorsEthanolRequest
): Promise<IFuelsIndicatorsEthanol> => {
  try {
    const response = await api.patch(`${BASE_URL}/${id}`, data);
    return response.data;
  } catch (error: any) {
    console.error(`Erro ao atualizar indicador de etanol com ID ${id}:`, error);

    if (error.response?.status === 404) {
      throw new Error(`Indicador de etanol não encontrado`);
    }

    if (error.response) {
      console.error('Status do erro:', error.response.status);
      console.error('Dados do erro:', error.response.data);
      throw new Error(
        `Erro ao atualizar indicador: ${error.response.data?.message || 'Erro de validação'}`
      );
    }

    throw handleApiError(error);
  }
};

/** Exclui um indicador de etanol */
export const deleteFuelsIndicatorsEthanol = async (id: string): Promise<void> => {
  try {
    await api.delete(`${BASE_URL}/${id}`);
  } catch (error: any) {
    console.error(`Erro ao excluir indicador de etanol com ID ${id}:`, error);

    if (error.response?.status === 404) {
      throw new Error(`Indicador de etanol não encontrado`);
    }

    throw handleApiError(error);
  }
};

/** Busca variação diária de etanol com filtros */
export const getDailyEthanolVariation = async (
  quantity: number = 5,
  baseDate?: string,
  ethanolType?: string
): Promise<IFuelsIndicatorsEthanol[]> => {
  try {
    const params = new URLSearchParams();
    params.append('quantity', quantity.toString());
    if (baseDate) params.append('baseDate', baseDate);
    if (ethanolType) params.append('ethanolType', ethanolType);

    const response = await api.get(`${BASE_URL}/daily-ethanol-variation?${params}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar variação diária de etanol:', error);
    throw handleApiError(error);
  }
};

/** Obtém a data mais recente disponível */
export const getMaxDateFromDatabase = async (): Promise<string> => {
  try {
    const response = await api.get(`${BASE_URL}/max-date`);
    return response.data.maxDate;
  } catch (error) {
    console.error('Erro ao buscar data máxima:', error);
    throw handleApiError(error);
  }
};

/** Obtém a próxima data disponível para navegação */
export const getNextAvailableDate = async (
  baseDate: string,
  direction: 'prev' | 'next',
  ethanolType?: string,
  unit: 'day' | 'week' | 'month' = 'day',
  amount: number = 1
): Promise<{ nextDate: string; hasNextDate: boolean }> => {
  try {
    const params = new URLSearchParams();
    params.append('baseDate', baseDate);
    params.append('direction', direction);
    params.append('unit', unit);
    params.append('amount', amount.toString());
    if (ethanolType) params.append('ethanolType', ethanolType);

    const response = await api.get(`${BASE_URL}/next-available-date?${params}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar próxima data disponível:', error);
    throw handleApiError(error);
  }
};

/** Obtém opções de filtro disponíveis */
export const getFilterOptions = async (): Promise<IFilterOptions> => {
  try {
    const response = await api.get(`${BASE_URL}/filter-options`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar opções de filtro:', error);
    throw handleApiError(error);
  }
};
